import React from 'react';
import { AIFunctionName, AIMessage } from '@saga/shared';

export type AIToolResponse = {
    action: string;
    error?: boolean;
    message?: string;
    requiresConfirmation?: boolean;
    [key: string]: any; // Allow additional properties for different tools
};

// Extract the tool call type from AIMessage
type AIToolCall = NonNullable<AIMessage['tool_calls']>[0];

type UseAIToolCallsOptions = {
    messages: AIMessage[];
    status: 'idle' | 'loading' | 'done' | 'error';
    history: any[];
    onToolCall: (toolName: AIFunctionName, toolCall: AIToolCall, response: AIToolResponse) => void;
};

/**
 * Hook to detect and handle AI tool calls in chat messages.
 * Only processes NEW messages from the current AI response to prevent duplicate executions.
 */
export function useAIToolCalls({ messages, status, history, onToolCall }: UseAIToolCallsOptions) {
    // Track the number of messages at the start of the request
    const historyLengthRef = React.useRef(history.length);

    // Update history length when sending a new message
    React.useEffect(() => {
        if (status === 'loading') {
            historyLengthRef.current = history.length;
        }
    }, [status, history.length]);

    // Check for tool calls in NEW messages only
    React.useEffect(() => {
        if (status === 'done' && messages.length > historyLengthRef.current) {
            // Only check messages that are part of the current AI response
            // Messages are prepended, so new messages are at the beginning
            const newMessagesCount = messages.length - historyLengthRef.current;
            const newMessages = messages.slice(0, newMessagesCount);

            // Look for assistant messages with tool_calls in the new messages
            for (const message of newMessages) {
                if (message.role === 'assistant' && message.tool_calls) {
                    for (const toolCall of message.tool_calls) {
                        // Find the corresponding tool response in new messages
                        const toolResponse = newMessages.find(
                            (m) => m.role === 'tool' && m.tool_call_id === toolCall.id,
                        );

                        if (toolResponse && toolResponse.content) {
                            // Some tools return non-JSON responses (like get_document_context returns XML)
                            // Only try to parse as JSON if it looks like JSON
                            if (
                                toolResponse.content.trim().startsWith('{') ||
                                toolResponse.content.trim().startsWith('[')
                            ) {
                                try {
                                    const response = JSON.parse(toolResponse.content) as AIToolResponse;
                                    onToolCall(toolCall.function.name as AIFunctionName, toolCall, response);
                                } catch (error) {
                                    console.error(
                                        `Failed to parse tool response for ${toolCall.function.name}:`,
                                        error,
                                    );
                                }
                            }
                            // For non-JSON responses (like XML from get_document_context), we don't need to call onToolCall
                            // as these are handled directly by the AI without user interaction
                        }
                    }
                }
            }
        }
    }, [messages, status, history.length, onToolCall]);
}

/**
 * Helper function to check if a tool response indicates an error
 */
export function isToolError(response: AIToolResponse): boolean {
    return response.error === true;
}

/**
 * Helper function to check if a tool response requires user confirmation
 */
export function requiresConfirmation(response: AIToolResponse): boolean {
    return response.requiresConfirmation === true;
}

/**
 * Example usage:
 *
 * const handleToolCall = useCallback((toolName, toolCall, response) => {
 *     switch (toolName) {
 *         case AIFunctionName.createPage:
 *             if (isToolError(response)) {
 *                 showErrorToast(response.message);
 *             } else if (requiresConfirmation(response)) {
 *                 setShowCreatePageDialog(true);
 *                 setPendingPageTitle(response.title);
 *             }
 *             break;
 *
 *         case AIFunctionName.updateTask:
 *             if (isToolError(response)) {
 *                 showErrorToast(response.message);
 *             } else if (response.action === 'update_task' && response.taskId) {
 *                 // Directly update the task
 *                 updateTask(response.taskId, response.updates);
 *             }
 *             break;
 *
 *         case AIFunctionName.searchDocuments:
 *             if (!isToolError(response) && response.results) {
 *                 setSearchResults(response.results);
 *             }
 *             break;
 *     }
 * }, []);
 *
 * useAIToolCalls({
 *     messages,
 *     status,
 *     history,
 *     onToolCall: handleToolCall,
 * });
 */
